import { resultCacheKey, useData } from '@mentimeter/core-hooks';
import {
  core,
  type QuestionRange,
  type SessionResult,
} from '@mentimeter/http-clients';
import type { QuestionWithSlide } from '@mentimeter/presentation-schema/api-types-overrides';
import { usePresentationRealtimeStore } from '@mentimeter/realtime';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import {
  PLACEHOLDER_RESULTS,
  POLLING_INTERVAL,
  QUESTION_TYPE_WITHOUT_RESULTS,
} from './constants';

export interface Result {
  result: SessionResult | undefined;
  range: QuestionRange;
}

interface ActiveResults {
  result: Result['result'];
  isPlaceholderResults: boolean;
  revalidate: () => void;
}
export interface UsePresentationResultsProps {
  slideDeckPublicKey: string | undefined;
  sessionId: number | 'current';
  question?: QuestionWithSlide | undefined;
}

export const usePresentationResults = ({
  slideDeckPublicKey,
  sessionId,
  question,
}: UsePresentationResultsProps): ActiveResults => {
  const connectionState = usePresentationRealtimeStore(
    (state) => state.connectionState,
  );

  const adminKey = question?.slideAdminKey;

  const cacheKey = adminKey ? resultCacheKey(adminKey, sessionId) : undefined;

  const fetcher = useCallback(async () => {
    invariant(adminKey, 'adminKey is required to fetch results');
    const { data } = await core().questions.result.get(
      adminKey,
      typeof sessionId === 'number' ? { session: sessionId } : undefined,
    );
    return data;
  }, [sessionId, adminKey]);

  const { data: resultData, revalidate } = useData<SessionResult | undefined>(
    {
      cacheKey,
      seriesId: slideDeckPublicKey,
      skip: Boolean(
        question &&
          Boolean(QUESTION_TYPE_WITHOUT_RESULTS.includes(question.type)),
      ),
      fetcher,
    },
    {
      refreshInterval:
        connectionState === 'failed' || connectionState === 'disconnected'
          ? POLLING_INTERVAL
          : 0,
    },
  );

  return {
    revalidate,
    result: resultData ?? PLACEHOLDER_RESULTS,
    isPlaceholderResults: !resultData,
  };
};
